import React from 'react'
import * as ArticleComponents from 'components/ArticleComponents'
import IconCarousel from 'components/IconCarousel'
import Grid from 'ds/Grid'
import styled from 'styled-components'
import { withPrefix } from 'utils/prefix'
import { typography, color, Icon } from '@primer-io/goat'
import Card from 'components/Card'
import Relative from 'ds/Relative'
import { Strong } from 'ds/TextGoat'

import {
	Button,
	Header,
	P,
	OverviewSection,
	HR,
	FullWidthGuide,
	OverviewTopSection,
	GuideLink,
	SmallGuideLink,
	FullWidthGuideContainer,
	FullWidthGuideImg,
	FullWidthGuideImgContainer,
	FullWidthGuideContent,
} from 'components/OverviewComponents'
import { LearnMoreButton } from 'components/LandingComponents'

import CoverImg from './assets/cover.png'
import HeadlessCheckoutImg from './assets/headless-checkout.png'
import DropInImg from './assets/drop-in.png'
import HowPaymentsImg from './assets/how-payments.png'
import CheckoutDashboardImg from './assets/checkout-dashboard.png'
import ClientSessionImg from './assets/client-session.svg'

import { featureGuides, integrationDifferencesTable, paymentMethods } from './data'
import { sdks } from '../../landing/data'

///////////////////////////////////////////
// Fancy Guide
///////////////////////////////////////////

const FancyGuideRoot = styled(FullWidthGuideContainer)`
	border: none;
	background: ${color('yellow-200')};

	flex-direction: column-reverse;
	${p => p.theme.above('medium')`
		flex-direction: row-reverse;
		`}
`

const FancyGuideTitle = styled(Header)`
	${typography('header-medium')}
`

const FancyGuideImg = styled(FullWidthGuideImg)`
	object-fit: contain;
	object-position: top left;
	padding: 0px 32px 32px 32px;

	${p => p.theme.above('medium')`
			object-fit: contain;
			object-position: center right;
			padding: 32px 32px 32px 12px;
		`}
`

const TransparentButton = styled(Button)`
	&.goat-Button_outlined {
		background-color: transparent;
	}
`

const FancyGuide = ({ title, imgSrc, buttonContent, to }) => (
	<FancyGuideRoot>
		<FullWidthGuideImgContainer>
			<FancyGuideImg src={imgSrc} />
		</FullWidthGuideImgContainer>
		<FullWidthGuideContent>
			<FancyGuideTitle style={{ paddingTop: 0 }}>{title}</FancyGuideTitle>

			<TransparentButton to={to} variant='outlined'>
				{buttonContent}
			</TransparentButton>
		</FullWidthGuideContent>
	</FancyGuideRoot>
)

const DifferencesTable = styled(ArticleComponents.HorizontalScrollTable)`
	border: 1px solid ${color('gray-200')};

	th {
		padding: 16px;
		background: ${color('gray-50')};

		${typography('title-small')}
		color: ${color('gray-900')};

		border-bottom: 1px solid ${color('gray-200')};
	}

	tr:not(:last-of-type) {
		border-bottom: 1px solid ${color('gray-200')};
	}

	td {
		${typography('body-medium')}
		color: ${color('gray-700')};
	}
`

///////////////////////////////////////////
// Root
///////////////////////////////////////////

const UniversalCheckoutOverview = () => {
	return (
		<ArticleComponents.Wrapper>
			<OverviewSection>
				<OverviewTopSection
					title='Universal Checkout'
					description={
						<>
							<ArticleComponents.P>
								Unify all your payment methods and checkout flows on Web and Mobile through a single SDK.
							</ArticleComponents.P>
						</>
					}
					imgSrc={CoverImg}
				/>
			</OverviewSection>

			<OverviewSection>
				{/* Flexibility */}
				<Header>Powerful ways to integrate</Header>
				<Grid cols={{ md: 2, lg: 2, xl: 2 }} gap={24} style={{ marginTop: 24 }}>
					<GuideLink
						title='Drop-in Checkout'
						description={
							<>
								<div>The simplest way to integrate Universal Checkout. </div>
								<div>Display a fully in-context checkout UI on Web and Mobile with just a few lines of code.</div>
							</>
						}
						link='../drop-in/get-started'
						imgSrc={DropInImg}
						coverAspectRatio={2.2}
						tag='Low Code'
					/>

					<GuideLink
						title='Headless Checkout'
						description={
							<>
								<div>The most versatile way to integrate Universal Checkout. </div>
								<div>
									Leverage the powerful capabilities of Universal Checkout with full control of the user experience.
								</div>
							</>
						}
						link='/payments/universal-checkout/headless/get-started'
						imgSrc={HeadlessCheckoutImg}
						coverAspectRatio={2.2}
						tag='Fully customizable'
					/>
				</Grid>

				<DifferencesTable style={{ marginTop: 24 }}>
					<thead>
						<tr>
							<th>Drop-in Checkout</th>
							<th>Headless Checkout</th>
						</tr>
					</thead>
					<tbody>
						{integrationDifferencesTable.map(({ dropIn, headless }, index) => (
							<tr key={index}>
								<td>{dropIn}</td>
								<td>{headless}</td>
							</tr>
						))}
					</tbody>
				</DifferencesTable>
			</OverviewSection>

			<OverviewSection>
				<Header>
					Easily roll out new payment methods
					<br /> as you expand your business
				</Header>

				<div style={{ overflow: 'hidden', marginBottom: 30 }}>
					<IconCarousel icons={paymentMethods} />
				</div>
				<LearnMoreButton label='Explore payment methods' to={'../../../payment-methods'} />
			</OverviewSection>

			<OverviewSection>
				<FullWidthGuide
					reverse
					title='No-code with all the flexibility'
					description='Configure payment methods and modules used in your checkout using our powerful Dashboard.'
					imgBackground={color('lilac-200')}
					imgSrc={CheckoutDashboardImg}
					buttonContent='Configure Universal Checkout'
					to={'../configure-universal-checkout-without-code'}
				/>
			</OverviewSection>

			{/* <OverviewSection>
				<FullWidthGuide
					title='Unify your checkout logic with client sessions'
					description='Pass the order details, customer information and additional metadata to our unified client session object to power all your payment services.'
					imgBackground={color('yellow-200')}
					imgSrc={ClientSessionImg}
					imgStyle={{ padding: 48 }}
					buttonContent='Manage client sessions'
					to='../manage-client-sessions'
				/>
			</OverviewSection> */}

			{/* To uncomment when SDK reference are a thing */}
			{/* <OverviewSection>
				<Header>Our SDKs have you covered</Header>
				<Grid cols={{ sm: 2, md: 4 }} gap={24} style={{ marginTop: 24 }}>
					{sdks.map(({ name, logo, ...rest }) => (
						<Card key={name} bgColor={color('gray-50')} {...rest} noArrow>
							<Relative left={-5}>{logo}</Relative>
							<Strong weight={500}>{name}</Strong>
						</Card>
					))}
				</Grid>
			</OverviewSection> */}
		</ArticleComponents.Wrapper>
	)
}

export default UniversalCheckoutOverview
